'use client';

import { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from '@/components/ui/AppIcon';

const RevenueAnalytics = () => {
  const revenueData = useMemo(() => [
    { month: 'Jan', revenue: 12450, commission: 1867 },
    { month: 'Feb', revenue: 15680, commission: 2352 },
    { month: 'Mar', revenue: 18920, commission: 2838 },
    { month: 'Apr', revenue: 16540, commission: 2481 },
    { month: 'May', revenue: 21340, commission: 3201 },
    { month: 'Jun', revenue: 24560, commission: 3684 },
  ], []);

  const commissionByPartnerData = useMemo(() => [
    { partner: 'Skyscanner', commission: 8947, bookings: 234 },
    { partner: 'Booking.com', commission: 7456, bookings: 198 },
    { partner: 'Expedia', commission: 5823, bookings: 156 },
  ], []);

  const stats = [
    { label: 'Total Revenue', value: '£109,490', change: '+18.2%', icon: 'CurrencyPoundIcon', trend: 'up' },
    { label: 'Total Commission', value: '£16,423', change: '+18.2%', icon: 'BanknotesIcon', trend: 'up' },
    { label: 'Avg. Commission/Booking', value: '£41.06', change: '+£2.34', icon: 'ChartBarIcon', trend: 'up' },
    { label: 'Commission Rate', value: '15.0%', change: '0%', icon: 'PercentBadgeIcon', trend: 'neutral' },
  ];

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => (
          <div key={index} className="bg-card rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-2">
              <Icon name={stat.icon as any} size={24} className="text-primary" />
              <span className={`text-sm font-medium ${
                stat.trend === 'up' ? 'text-success' : stat.trend === 'neutral' ? 'text-muted-foreground' : 'text-error'
              }`}>
                {stat.change}
              </span>
            </div>
            <div className="text-2xl font-bold text-foreground mb-1">{stat.value}</div>
            <div className="text-sm text-muted-foreground">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Revenue Trend */}
      <div className="bg-card rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-bold text-foreground mb-4">Revenue & Commission Trend</h3>
        <ResponsiveContainer width="100%" height={350}>
          <LineChart data={revenueData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="revenue" stroke="#3B82F6" strokeWidth={2} name="Total Revenue (£)" />
            <Line type="monotone" dataKey="commission" stroke="#10B981" strokeWidth={2} name="Commission Earned (£)" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Commission by Partner */}
      <div className="bg-card rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-bold text-foreground mb-4">Commission by Partner (Last 30 Days)</h3>
        <div className="space-y-4">
          {commissionByPartnerData.map((partner, index) => (
            <div key={index}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="font-medium text-foreground">{partner.partner}</div>
                  <div className="text-sm text-muted-foreground">{partner.bookings} bookings</div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-success">£{partner.commission.toLocaleString()}</div>
                  <div className="text-sm text-muted-foreground">£{(partner.commission / partner.bookings).toFixed(2)}/booking</div>
                </div>
              </div>
              <div className="w-full bg-surface rounded-full h-2">
                <div
                  className="bg-primary rounded-full h-2 transition-all duration-500"
                  style={{ width: `${(partner.commission / 8947) * 100}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Revenue Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-bold text-foreground mb-4">Revenue by Service</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-foreground">Flight Bookings</span>
              <span className="text-sm font-bold text-foreground">£67,890 (62%)</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-foreground">Hotel Bookings</span>
              <span className="text-sm font-bold text-foreground">£28,450 (26%)</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-foreground">Holiday Packages</span>
              <span className="text-sm font-bold text-foreground">£13,150 (12%)</span>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-bold text-foreground mb-4">Top Revenue Routes</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-foreground">London-Dubai</span>
              <span className="text-sm font-bold text-foreground">£18,450</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-foreground">London-New York</span>
              <span className="text-sm font-bold text-foreground">£15,680</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-foreground">Manchester-Dubai</span>
              <span className="text-sm font-bold text-foreground">£12,340</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RevenueAnalytics;